import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, PlusIcon } from 'lucide-react';
import { readableTextOn } from '../../../utils/theme';

/* ------------------------------------------------------------------ */
/* Accordion section — one topic, a live summary, opens in place        */
/* ------------------------------------------------------------------ */

interface DesignSectionProps {
  icon: React.ElementType;
  title: string;
  /** What is currently chosen, readable at a glance while closed. */
  summary: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}
export function DesignSection({
  icon: Icon,
  title,
  summary,
  open,
  onToggle,
  children
}: DesignSectionProps) {
  const id = `design-${title.toLowerCase().replace(/\W+/g, '-')}`;
  return <section className={`rounded-2xl border bg-white transition-colors duration-150 ease-out ${open ? 'border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)]' : 'border-slate-200/70 hover:border-slate-300'}`} data-id="element-3455" data-mp="3pvrpi1wlwo2n">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={id} className="w-full flex items-center gap-3.5 px-4 py-3.5 text-left" data-id="element-3456" data-mp="1u91cuwzt0mog">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-150 ease-out ${open ? 'bg-primary-indigo text-white' : 'bg-slate-50 text-slate-500'}`} data-id="element-3457" data-mp="1kd6s9wc0shj1">
          <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} data-id="element-3458" data-mp="2d1caocflpufp" />
        </span>
        <span className="flex-1 min-w-0" data-id="element-3459" data-mp="2889hi5eqkn01">
          <span className="block text-[14.5px] font-semibold text-dark-navy" data-id="element-3460" data-mp="23ar4a2torlm8">
            {title}
          </span>
          <span className="flex items-center gap-1.5 text-[12.5px] text-slate-500 mt-0.5 truncate" data-id="element-3461" data-mp="30qok9deuc4ij">
            {summary}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} data-id="element-3462" data-mp="2vy3uqo2ywdkt" />
      </button>
      <AnimatePresence initial={false} data-id="element-3463" data-mp="yzo4j9p21ceq">
        {open && <motion.div id={id} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-3464" data-mp="kccujc8iu0gq">
            <div className="px-4 pb-5 pt-1 space-y-6" data-id="element-3465" data-mp="fd8k6i9v1wot">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

export function ControlLabel({
  children,
  hint
}: {
  children: React.ReactNode;
  hint?: string;
}) {
  return <div className="mb-2.5" data-id="element-3466" data-mp="3l840dce5pfxz">
      <p className="text-[13px] font-semibold text-dark-navy" data-id="element-3467" data-mp="3l9f2l7ryefmz">{children}</p>
      {hint && <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-3468" data-mp="3ga25zvba5u77">{hint}</p>}
    </div>;
}

/** A row of visual options. Each option renders its own preview. */
export function VisualChoice<T extends string>({
  value,
  onChange,
  options,
  columns = 3
}: {
  value: T;
  onChange: (v: T) => void;
  options: {
    id: T;
    label: string;
    preview: React.ReactNode;
  }[];
  columns?: 2 | 3 | 4 | 5;
}) {
  const cols = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5'
  }[columns];
  return <div role="radiogroup" className={`grid ${cols} gap-2`} data-id="element-3469" data-mp="hqluvbluwl0q">
      {options.map(o => {
      const active = o.id === value;
      return <button key={o.id} type="button" role="radio" aria-checked={active} onClick={() => onChange(o.id)} className="group text-center" data-id="element-3470" data-mp="cqpq3a5c4pk4">
            <span className={`relative block rounded-xl border-2 overflow-hidden transition-colors duration-150 ease-out ${active ? 'border-primary-indigo' : 'border-transparent ring-1 ring-slate-200 group-hover:ring-slate-300'}`} data-id="element-3471" data-mp="15ni3mcyiwjrq">
              {o.preview}
              {active && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary-indigo text-white flex items-center justify-center" data-id="element-3472" data-mp="1jn1c56v8xx4k">
                  <CheckIcon className="w-2.5 h-2.5" strokeWidth={3.5} data-id="element-3473" data-mp="mybpz4cl6ics" />
                </span>}
            </span>
            <span className={`block text-[12px] mt-1.5 leading-tight ${active ? 'font-semibold text-dark-navy' : 'text-slate-500'}`} data-id="element-3474" data-mp="vx6wkhjjlugm">
              {o.label}
            </span>
          </button>;
    })}
    </div>;
}

/** A compact pill group for short text choices. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label
}: {
  value: T;
  onChange: (v: T) => void;
  options: {
    id: T;
    label: React.ReactNode;
  }[];
  label: string;
}) {
  return <div role="radiogroup" aria-label={label} className="flex p-1 bg-slate-100 rounded-xl gap-1" data-id="element-3475" data-mp="3rtv3lezsjzyc">
      {options.map(o => {
      const on = o.id === value;
      return <button key={o.id} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.id)} className={`flex-1 px-2 py-1.5 rounded-lg text-[12.5px] font-semibold whitespace-nowrap transition-colors duration-150 ease-out ${on ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3476" data-mp="3iuhqmccgkt8v">
            {o.label}
          </button>;
    })}
    </div>;
}

/** Dashed "add" row used at the end of editable lists. */
export function AddRow({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-slate-300 text-[13px] font-semibold text-slate-500 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3477" data-mp="2q5syh2tdry99">
      <PlusIcon className="w-4 h-4" data-id="element-3478" data-mp="26llqtcsxr9vx" />
      {label}
    </button>;
}

/** Subtle → Strong style slider with end labels. */
export function RangeControl({
  value,
  onChange,
  low,
  high,
  label
}: {
  value: number;
  onChange: (v: number) => void;
  low: string;
  high: string;
  label: string;
}) {
  return <div className="flex items-center gap-3" data-id="element-3479" data-mp="2pi3gloo84zpj">
      <span className="text-[12px] text-slate-400 w-12" data-id="element-3480" data-mp="2phbt714s68ac">{low}</span>
      <input type="range" min={0} max={100} value={value} onChange={e => onChange(Number(e.target.value))} className="flex-1 accent-primary-indigo" aria-label={label} data-id="element-3481" data-mp="2ugoc5asj6v7n" />
      <span className="text-[12px] text-slate-400 w-12 text-right" data-id="element-3482" data-mp="3mz23crjd3ixr">{high}</span>
    </div>;
}
export function SwitchRow({
  title,
  desc,
  checked,
  onChange
}: {
  title: string;
  desc?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className="flex items-start gap-4" data-id="element-3483" data-mp="1l8l4e17hp96e">
      <div className="flex-1 min-w-0" data-id="element-3484" data-mp="1l7icpr2cf2pf">
        <p className="text-[13.5px] font-semibold text-dark-navy" data-id="element-3485" data-mp="1g9gt951091df">{title}</p>
        {desc && <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed" data-id="element-3486" data-mp="sje53007ofk3">
            {desc}
          </p>}
      </div>
      <button type="button" role="switch" aria-checked={checked} aria-label={title} onClick={() => onChange(!checked)} className={`relative w-10 h-6 rounded-full flex-shrink-0 transition-colors duration-150 ease-out ${checked ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-3487" data-mp="nll20fu7g1o1">
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-[left] duration-150 ease-out ${checked ? 'left-[18px]' : 'left-0.5'}`} data-id="element-3488" data-mp="2cxoa7lf0yrl5" />
      </button>
    </div>;
}

/* ------------------------------------------------------------------ */
/* Colour picker — swatches first, exact code only on request          */
/* ------------------------------------------------------------------ */

export const SWATCHES = ['#4C7FC1', '#25446F', '#0F766E', '#B45309', '#BE123C', '#6D28D9', '#0B1220', '#F5CE3E', '#F97316', '#22C55E'];
export function ColorRow({
  label,
  hint,
  value,
  onChange
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [exact, setExact] = useState(false);
  const current = value.toLowerCase();
  return <div data-id="element-3489" data-mp="13ypkuta1dc61">
      <div className="flex items-start justify-between gap-3 mb-2.5" data-id="element-3490" data-mp="13zisdwgtk5p8">
        <div data-id="element-3491" data-mp="30r99xj98vgyr">
          <p className="text-[13px] font-semibold text-dark-navy" data-id="element-3492" data-mp="30rj1hb9xtw6k">{label}</p>
          {hint && <p className="text-[12px] text-slate-500 mt-0.5" data-id="element-3493" data-mp="35qchpbgm01af">{hint}</p>}
        </div>
        <button type="button" onClick={() => setExact(e => !e)} className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11.5px] font-mono text-slate-500 hover:bg-slate-50 transition-colors duration-150 ease-out" aria-label={`Enter an exact code for ${label.toLowerCase()}`} data-id="element-3494" data-mp="35sgktr6oow9n">
          <span className="w-3 h-3 rounded-full border border-black/10" style={{
          background: value
        }} data-id="element-3495" data-mp="194cvsimaoala" />
          {value.toUpperCase()}
        </button>
      </div>
      <div className="flex flex-wrap gap-2" data-id="element-3496" data-mp="21rz0ju79dl9q">
        {SWATCHES.map(s => {
        const on = current === s.toLowerCase();
        return <button key={s} type="button" onClick={() => onChange(s)} aria-label={s} aria-pressed={on} className={`w-7 h-7 rounded-full flex items-center justify-center transition-transform duration-150 ease-out hover:scale-110 ${on ? 'ring-2 ring-offset-2 ring-dark-navy' : 'ring-1 ring-black/10'}`} style={{
          background: s
        }} data-id="element-3497" data-mp="1wrkqk6m3k192">
              {on && <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} style={{
            color: readableTextOn(s)
          }} data-id="element-3498" data-mp="2kwqypytd39q3" />}
            </button>;
      })}
        <label className="w-7 h-7 rounded-full border border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary-indigo/60 transition-colors duration-150 ease-out" title="Any colour" data-id="element-3499" data-mp="2usu8nquggcnl">
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-0 h-0 opacity-0" aria-label={`Pick any ${label.toLowerCase()}`} data-id="element-3500" data-mp="3ik9ehtlfxc3l" />
          <PlusIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3501" data-mp="1o68gfaxpst4k" />
        </label>
      </div>
      {exact && <div className="mt-3 flex items-center w-36 rounded-lg border border-slate-200 focus-within:border-primary-indigo/50 overflow-hidden" data-id="element-3502" data-mp="1j8gdzmzg1om4">
          <span className="pl-3 text-sm text-slate-400 font-mono" data-id="element-3503" data-mp="3nefc1mkkeou">#</span>
          <input defaultValue={value.replace('#', '')} onChange={e => {
        const v = e.target.value.trim().replace('#', '');
        if (/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) onChange(`#${v}`);
      }} className="w-full px-1.5 py-2 text-sm font-mono outline-none uppercase" aria-label={`${label} code`} data-id="element-3504" data-mp="3ph46ibzk1rz" />
        </div>}
    </div>;
}